import { Button, Combobox, Field, InlineMessage, Input, Modal, ModalContent, OptionSelect, SummaryTable } from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import {
  addMonths,
  addedText,
  DEFAULT_SETTINGS,
  isDeal,
  monthIndex,
  monthName,
  monthRange,
  type NewForm,
  newFormModel,
  Planner,
  workOptions,
} from './model';
import { createAssignments, deleteAssignment, fetchResourcing, type ResourcingData } from './resourcingApi';

interface Props {
  /** The page's payload: the work, roles and people to choose from. */
  data: ResourcingData;
  initial: NewForm;
  onClose: () => void;
  onCreated: (ids: string[], text: string) => void;
}

/** The form's starting values: an opportunity first, a role the person fills, this month and the two after. */
export function defaultForm(data: ResourcingData, preset: { person?: string; role?: string } = {}): NewForm {
  const options = workOptions(data);
  const person = preset.person ? data.people.find((p) => p.id === preset.person) : undefined;
  const role = preset.role ?? person?.primary_role_id ?? person?.role_ids[0] ?? data.roles.find((r) => r.active)?.id ?? '';
  const work = options[0]?.value ?? '';
  const current = data.current_month;
  return { work, role, person: person?.id ?? '', from: current, to: addMonths(current, 2), hours: '40', overrides: {} };
}

/** New assignment: hours of a role on a project or opportunity, repeated over a range of months, each month editable, its effect shown as it is typed. */
export function NewAssignmentDialog({ data, initial, onClose, onCreated }: Props): JSX.Element {
  const [form, setForm] = useState<NewForm>(initial);
  const [preview, setPreview] = useState<ResourcingData | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const set = (patch: Partial<NewForm>) => setForm((f) => ({ ...f, ...patch }));
  const options = useMemo(() => workOptions(data), [data]);
  const shownOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => `${o.label} ${o.hint}`.toLowerCase().includes(q)) : options;
  }, [options, query]);

  // The preview reads the months the form names, which may lie outside the page's.
  const valid = monthIndex(form.to) >= monthIndex(form.from) && monthIndex(form.to) - monthIndex(form.from) < 36;
  useEffect(() => {
    if (!valid) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      fetchResourcing(authenticatedFetch, form.from, form.to)
        .then((d) => live && setPreview(d))
        .catch(() => live && setPreview(null));
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [form.from, form.to, valid]);

  const planner = useMemo(() => new Planner(preview && preview.from === form.from && preview.to === form.to ? preview : data, DEFAULT_SETTINGS), [preview, data, form.from, form.to]);
  const model = newFormModel(planner, form);
  const work = planner.workByKey.get(form.work);
  const roles = data.roles.filter((r) => r.active || r.id === form.role);
  const monthOptions = monthRange(addMonths(data.current_month, -3), addMonths(data.current_month, 23)).map((m) => ({ value: m, label: monthName(m) }));

  const pickWork = (value: string) => {
    setQuery('');
    const next = data.work.find((w) => `${w.kind}:${w.id}` === value);
    const patch: Partial<NewForm> = { work: value };
    if (next?.first_month && next.last_month) {
      const from = monthIndex(next.first_month) > monthIndex(data.current_month) ? next.first_month : data.current_month;
      if (monthIndex(next.last_month) >= monthIndex(from)) Object.assign(patch, { from, to: next.last_month });
    }
    set(patch);
  };

  const create = async () => {
    if (model.error || !work) return;
    setSaving(true);
    setFailure(null);
    // One create per run of months with the same hours; if one fails, the ones before it are taken back.
    const ids: string[] = [];
    try {
      for (const run of model.runs) {
        const created = await createAssignments(authenticatedFetch, {
          ...(work.kind === 'project' ? { project_id: work.id } : { opportunity_id: work.id }),
          resource_role_id: form.role,
          app_user_id: form.person === '' ? null : form.person,
          from: run.from,
          to: run.to,
          hours: run.hours,
        });
        ids.push(...created.ids);
      }
      onCreated(ids, addedText(planner, form, model.runs));
    } catch (error) {
      await Promise.allSettled(ids.map((id) => deleteAssignment(authenticatedFetch, id)));
      setFailure(error instanceof Error ? error.message : 'Could not add the assignment.');
      setSaving(false);
    }
  };

  return (
    <Modal open onOpenChange={(open) => (open ? undefined : onClose())}>
      <ModalContent
        size="lg"
        title={
          <>
            <span className="oly-eyebrow block">Resourcing</span>
            <span className="mt-1 block">New assignment</span>
          </>
        }
        footer={
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="brand" onClick={create} disabled={!!model.error} loading={saving}>{model.createText}</Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <Field label="Project or Opportunity">
            <Combobox
              mode="options"
              aria-label="Project or opportunity"
              options={shownOptions}
              value={form.work}
              onValueChange={pickWork}
              onQueryChange={setQuery}
              formatValue={(value: string) => options.find((o) => o.value === value)?.label ?? ''}
              placeholder="Search projects and opportunities"
              emptyLabel="Nothing matches"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
            <Field label="Role">
              <OptionSelect
                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                value={form.role}
                onValueChange={(role) => set({ role, person: '' })}
                placeholder="Choose a role"
              />
            </Field>
            <Field label="Person">
              <OptionSelect
                options={model.eligible.map((p) => ({ value: p.id, label: p.name }))}
                value={form.person}
                onValueChange={(person) => set({ person })}
                emptyLabel={work && !isDeal(work) ? 'Choose a person' : 'Not named yet (forecast)'}
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-1">
            <Field label="From">
              <OptionSelect options={monthOptions} value={form.from} onValueChange={(from) => set({ from })} />
            </Field>
            <Field label="Through">
              <OptionSelect options={monthOptions} value={form.to} onValueChange={(to) => set({ to })} />
            </Field>
            <Field label="Hours Per Month">
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                step={5}
                value={form.hours}
                onChange={(event) => set({ hours: event.target.value, overrides: {} })}
                endSlot="h"
              />
            </Field>
          </div>
          {model.statusNote ? <div className="text-xs text-[var(--fg-muted)]">{model.statusNote}</div> : null}
          {model.rows.length ? (
            <div className="flex flex-col gap-1.5">
              <SummaryTable
                caption="Hours by month and their effect"
                columns={[
                  { key: 'month', label: 'Month' },
                  { key: 'hours', label: 'Hours' },
                  { key: 'role', label: 'Role Effect' },
                  { key: 'overall', label: 'Overall Effect' },
                ]}
                rows={model.rows.map((row) => ({
                  key: row.month,
                  cells: {
                    month: row.label,
                    hours: (
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step={5}
                        className="w-24"
                        aria-label={`Hours in ${row.label}`}
                        value={row.hours}
                        onChange={(event) => setForm((f) => ({ ...f, overrides: { ...f.overrides, [row.month]: event.target.value } }))}
                        endSlot="h"
                      />
                    ),
                    role: { text: row.role.text, tone: row.role.warn ? 'negative' : undefined },
                    overall: { text: row.overall.text, tone: row.overall.warn ? 'negative' : undefined },
                  },
                }))}
              />
              <div className="text-xs text-[var(--fg-muted)]">{model.effectNote}</div>
            </div>
          ) : null}
          {model.error ? (
            <div className="rounded-[var(--radius-md)] bg-[var(--heat-over-1)] px-2.5 py-2 text-xs text-[var(--heat-over-fg)]">{model.error}</div>
          ) : null}
          {failure ? <InlineMessage tone="error" role="alert">{failure}</InlineMessage> : null}
        </div>
      </ModalContent>
    </Modal>
  );
}
